/**
 * 法條編號允許之拉丁後綴（大小寫不敏感；括號內數字另由 (n) 規則匹配，不併入後綴）。
 * 長詞在前，避免短詞誤先匹配。
 */
const LATIN_SUFFIX_ALT =
  'Quatuordecim|Quinquies|Tredecim|Duadecim|Undecim|Quarter|Quatrum|Quattuor|Quinque|Atharas|Sattras|Sexies|Septies|Octies|Septum|Septem|Quater|Quarta|Novum|Novem|Decem|Decum|Solas|Caput|Octo|Bis|Tres|Ter|Sex';

const latinSlashOrSuffix = `(?:\\/\\d+|\\/(?:${LATIN_SUFFIX_ALT})\\b)`;
const latinSpaceSuffix = `(?:\\s+(?:${LATIN_SUFFIX_ALT})\\b)`;

export default {
  modelName: 'article',
  versionModelName: 'article_version',
  versionTimestampKey: 'announce_at',
  label: '法條',
  fields: {
    last_version: {
      type: 'last_version',
      fields: {
        no_number: {
          type: 'text',
          label: '條文數字',
          sm: 12,
          rules: 'required',
          maxlength: 10,
        },
        announce_at: {
          type: 'date',
          label: '發布日期',
          maxlength: 100,
          sm: 6,
        },
        effect_at: {
          type: 'date',
          label: '生效日期',
          rules: 'required',
          maxlength: 100,
          sm: 6,
        },
        attaches: {
          type: 'files',
          label: '附件',
          uploadUrl: '/article_version/attach/',
        },
        reference_link: {
          type: 'link',
          label: '來源連結',
          maxlength: 500,
        },
        effects: {
          label: '風險燈號',
          type: 'hasMany',
          modelName: 'effect',
          textKey: 'name',
          conditionKeys: [
            {
              fieldName: 'areas',
              conditionName: 'area',
              type: 'array',
            },
          ],
        },
        is_active: {
          type: 'switch',
          sm: 6,
          label: '啟用',
          defaultValue: true,
        },
        act_status: {
          label: '狀態',
          type: 'belongsTo',
          textKey: 'name',
          modelName: 'act_status',
          rules: 'required',
          sm: 6,
          conditionKeys: [
            {
              fieldName: 'areas',
              conditionName: 'area',
              type: 'array',
            },
          ],
        },
        uploadFileToStorage: {
          type: 'files',
          label: '上傳檔案到後台資料庫,點擊複製連結',
          uploadUrl: '/article_version/image/',
          isLink: false,
          customFunction: (url) => {
            navigator.clipboard.writeText(url).then(() => {
              alert('成功複製到剪貼板');
            }).catch(err => {
              alert('複製失敗:', err);
            });
          }
        },
        locales: {
          type: 'locales',
          // label: '多語言內容',
          sm: 12,
          fields: {
            book: {
              type: 'text',
              label: '冊',
              maxlength: 2000,
            },
            part: {
              type: 'text',
              label: '篇',
              maxlength: 2000,
            },
            chapter: {
              type: 'text',
              label: '章',
              maxlength: 2000,
            },
            division: {
              type: 'text',
              label: '部',
              maxlength: 2000,
            },
            section: {
              type: 'text',
              label: '節',
              maxlength: 2000,
            },
            sub_section: {
              type: 'text',
              label: '副節',
              maxlength: 2000,
            },
            no_text: {
              type: 'text',
              label: '條文文字',
              rules: 'required',
              maxlength: 2000,
            },
            content: {
              type: 'textarea',
              label: '法條內容',
              rules: 'required',
              maxlength: 50000,
            },
            rich_content: {
              type: 'editor',
              label: '法條內容 (含HTML)',
              maxlength: 50000,
            },
            official_comment: {
              type: 'textarea',
              label: '立法理由',
              maxlength: 10000,
            },
            ll_comment: {
              type: 'textarea',
              label: 'ESGoal評析',
              maxlength: 10000,
            },
          },
        },
      },
    },
  },
  fieldsRead: [
    'last_version.act_status',
    'last_version.locales',
    'last_version.announce_at',
    'last_version.effect_at',
    'last_version.attaches',
    'last_version.effects',
  ],
  parseFields: [
    {
      type: 'select',
      label: '解析冊類型',
      items: [
        {
          value: /(第 *[0-9]+ *[冊])(.*)/i,
          text: '第1冊',
        },
        {
          value: /(Book *[IVXLCDM]+)(.*)/i,
          text: 'Book I',
        },
      ],
      valueKey: 'book',
      compareLabel: '冊',
    },
    {
      type: 'select',
      label: '解析篇類型',
      items: [
        {
          value: /(第 *[零一二三四五六七八九十百千]+ *[篇])(.*)/i,
          text: '第一篇',
        },
        {
          value: /(第 *[0-9]+ *[篇])(.*)/i,
          text: '第1篇',
        },
        {
          value: /(第 *[0-9]+ *[編])(.*)/i,
          text: '第1編',
        },
        {
          value: /(Phần *[IVXLCDM]+)(.*)/i,
          text: 'Phần I',
        },
        {
          value: /(PART\s+[A-Z]+)(.*)/i,
          text: 'PART ONE',
        },
        {
          value: /(Title *[IVXLCDM]+)(.*)/i,
          text: 'Title I',
        },
        {
          value: /(Title *\d+)(.*)/i,
          text: 'Title 1',
        },
      ],
      valueKey: 'part',
      compareLabel: '篇',
    },
    {
      type: 'select',
      label: '解析章類型',
      items: [
        {
          value: /(第 *[零一二三四五六七八九十百千]+ *[章])(.*)/i,
          text: '第一章',
        },
        // 第 N 章、第 N/N 章、第 N 拉丁後綴 章、第 N XXX (n) 章（與條文規則一致）
        {
          value: new RegExp(
            `(第\\s*\\d+${latinSlashOrSuffix}?${latinSpaceSuffix}?(?:\\s*\\(\\s*\\d+\\s*\\))?\\s*章)(.*)`,
            'i',
          ),
          text: '第 1 章 數字、N/N、拉丁後綴、/(n)',
        },
        {
          value: /(Chương *[IVXLCDM]+)(.*)/i,
          text: 'Chương I',
        },
        {
          value: /(Chương *\d+)(.*)/i,
          text: 'Chương 1',
        },
        // Chapter N、Chapter N/N、Chapter N 拉丁後綴、Chapter N XXX (n)（與 Part 規則一致，含羅馬數字）
        {
          value: new RegExp(
            `(Chapter\\s+(?:\\d+|[IVXLCDM]+)${latinSlashOrSuffix}?${latinSpaceSuffix}?(?:\\s*\\(\\s*\\d+\\s*\\))?)(.*)`,
            'i',
          ),
          text: 'Chapter 數字、羅馬數字、N/N、拉丁後綴、/(n)',
        },
      ],
      valueKey: 'chapter',
      compareLabel: '章',
    },
    {
      type: 'select',
      label: '解析部類型',
      items: [
        {
          value: /(第 *[零一二三四五六七八九十百千]+ *部)(.*)/i,
          text: '第一部',
        },
        // 第 N 部、第 N/N 部、第 N 拉丁後綴 部、第 N XXX (n) 部（與條文規則一致）
        {
          value: new RegExp(
            `(第\\s*\\d+${latinSlashOrSuffix}?${latinSpaceSuffix}?(?:\\s*\\(\\s*\\d+\\s*\\))?\\s*部)(.*)`,
            'i',
          ),
          text: '第 1 部 數字、N/N、拉丁後綴、/(n)',
        },
        // 第 N 編、第 N/N 編、第 N 拉丁後綴 編、第 N XXX (n) 編（與第 N 部規則一致）
        {
          value: new RegExp(
            `(第\\s*\\d+${latinSlashOrSuffix}?${latinSpaceSuffix}?(?:\\s*\\(\\s*\\d+\\s*\\))?\\s*編)(.*)`,
            'i',
          ),
          text: '第 1 編 數字、N/N、拉丁後綴、/(n)',
        },
        // Part N、Part N/N、Part N 拉丁後綴、Part N XXX (n)（與第 N 部規則一致）
        {
          value: new RegExp(
            `(Part\\s+(?:\\d+|[IVXLCDM]+)${latinSlashOrSuffix}?${latinSpaceSuffix}?(?:\\s*\\(\\s*\\d+\\s*\\))?)(.*)`,
            'i',
          ),
          text: 'Part 羅馬數字、數字、N/N、拉丁後綴、/(n)',
        },
        // Division N、Division N/N、Division N 拉丁後綴、Division N XXX (n)（與第 N 部規則一致）
        {
          value: new RegExp(
            `(Division\\s+(?:\\d+|[IVXLCDM]+)${latinSlashOrSuffix}?${latinSpaceSuffix}?(?:\\s*\\(\\s*\\d+\\s*\\))?)(.*)`,
            'i',
          ),
          text: 'Division 羅馬數字、數字、N/N、拉丁後綴、/(n)',
        },
      ],
      valueKey: 'division',
      compareLabel: '部',
    },
    {
      type: 'select',
      label: '解析節類型',
      items: [
        {
          value: /(第 *[零一二三四五六七八九十百千]+ *[節])(.*)/i,
          text: '第一節',
        },
        // 第 N 節、第 N/N 節、第 N 拉丁後綴 節、第 N XXX (n) 節（與條文規則一致）
        {
          value: new RegExp(
            `(第\\s*\\d+${latinSlashOrSuffix}?${latinSpaceSuffix}?(?:\\s*\\(\\s*\\d+\\s*\\))?\\s*節)(.*)`,
            'i',
          ),
          text: '第 1 節 數字、N/N、拉丁後綴、/(n)',
        },
        {
          value: /(Mục *\d+)(.*)/i,
          text: 'Mục 1',
        },
        // Section N、Section N/N、Section N 拉丁後綴、Section N XXX (n)（與第 N 節規則一致）
        {
          value: new RegExp(
            `(Section\\s+\\d+${latinSlashOrSuffix}?${latinSpaceSuffix}?(?:\\s*\\(\\s*\\d+\\s*\\))?\\.?)(.*)`,
            'i',
          ),
          text: 'Section 數字、N/N、拉丁後綴、/(n)',
        },
      ],
      valueKey: 'section',
      compareLabel: '節',
    },
    {
      type: 'select',
      label: '解析副節類型',
      items: [
        {
          value: /(Tiểu mục *\d+\.?)(.*)/i,
          text: 'Tiểu mục 1.',
        },
        {
          value: /(Sub-section *\d+\.?)(.*)/i,
          text: 'Sub-section 1.',
        },
      ],
      valueKey: 'sub_section',
      compareLabel: '副節',
    },
    {
      type: 'select',
      label: '解析條文類型',
      items: [
        {
          value: /([零一二三四五六七八九十百千]+)、(.*)/i,
          text: '一、',
        },
        {
          value: /(第 *[零一二三四五六七八九十百千]+ *[条])(.*)/i,
          text: '第一条',
        },
        {
          value: /(第 *[零一二三四五六七八九十百千]+ *[点])(.*)/i,
          text: '第一点',
        },
        {
          value:
            /(?<!\S)(第 *\s*[零一二三四五六七八九十百千○〇]+(?:\/[零一二三四五六七八九十百千○〇\dA-Za-z]+)?(?:之[零一二三四五六七八九十百千○〇\dA-Za-z]+)? *\s*[條](?:之[零一二三四五六七八九十百千○〇\dA-Za-z]+)?)(?=(?:\s|$|[:：]))[:：]?\s*(.*)/i,
          text: '第一條、第一/一條、第一之一條、第一條之一',
        },
        // 第 N 條、第 N/N 條、第 N XXX (n) 條、第 N 拉丁後綴 條（如 第 3 Ter (3) 條、第 9 Bis 條、第 67/bis (2) 條）
        {
          value: new RegExp(
            `(第\\s*\\d+${latinSlashOrSuffix}?${latinSpaceSuffix}?(?:\\s*\\(\\s*\\d+\\s*\\))?\\s*條)(.*)`,
            'i',
          ),
          text: '第 1 條 數字、N/N、拉丁後綴、/(n)',
        },
        {
          value: /(Điều *\d+)(.*)/i,
          text: 'Điều 1',
        },
        // Article N、Article N/N、Article N 拉丁後綴、Article N XXX (n)（與條文規則一致）
        {
          value: new RegExp(
            `(Article\\s+\\d+${latinSlashOrSuffix}?${latinSpaceSuffix}?(?:\\s*\\(\\s*\\d+\\s*\\))?\\.?)(.*)`,
            'i',
          ),
          text: 'Article 數字、N/N、拉丁後綴、/(n)',
        },
        // Section N、Section N/N、Section N 拉丁後綴、(n)、Section N/xxx
        {
          value: new RegExp(
            `(Section\\s+\\d+\\.?${latinSlashOrSuffix}?${latinSpaceSuffix}?(?:\\s*\\(\\s*\\d+\\s*\\))?)(.*)`,
            'i',
          ),
          text: 'Section 1 數字、N/N、拉丁後綴、/(n)',
        },
        // Clause N、Clause N/N、Clause N 拉丁後綴、Clause N XXX (n)（與條文規則一致）
        {
          value: new RegExp(
            `(Clause\\s+\\d+${latinSlashOrSuffix}?${latinSpaceSuffix}?(?:\\s*\\(\\s*\\d+\\s*\\))?\\.?)(.*)`,
            'i',
          ),
          text: 'Clause 數字、N/N、拉丁後綴、/(n)',
        },
      ],
      valueKey: 'no_text',
      compareLabel: '條',
    },
    {
      type: 'select',
      label: '條次同行文字處理',
      items: [
        {
          value: 'asTitle',
          text: '條次後方文字視為「標題」',
        },
        {
          value: 'asContent',
          text: '條次後方文字視為「內容」',
        },
      ],
      valueKey: 'articleInlineTextRule',
      compareLabel: '條次同行文字處理',
      defaultValue: 'asTitle',
      clearable: false,
    }
  ],
  localeFields: [
    {
      valueKey: 'baselineLocale',
      label: '基準語言',
    },
    {
      valueKey: 'translateLocale',
      label: '增譯語言',
    },
  ],
  otherFields: [
    {
      type: 'link',
      valueKey: 'reference_link',
      label: '來源連結',
      maxlength: 500,
    },
  ],
};
